import React, { useEffect, useState } from "react";
import { Mail, Phone, Rocket, Wrench } from "lucide-react";
import Logo from "./Logo";
import { useContent } from "../context/ContentContext";

export const STATUS_DEFAULTS = {
  coming_soon: {
    pill: "Coming Soon",
    title: "Something great is on its way",
    message:
      "Novocreste International is preparing its new website. Global pharmaceutical sourcing, made simple — launching very soon.",
  },
  maintenance: {
    pill: "Under Maintenance",
    title: "We'll be back shortly",
    message:
      "We're making a few improvements to our website. Thank you for your patience — for anything urgent, please reach us directly.",
  },
};

function useCountdown(target) {
  const end = target ? new Date(target).getTime() : NaN;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (Number.isNaN(end)) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [end]);
  if (Number.isNaN(end) || end <= now) return null;
  const s = Math.floor((end - now) / 1000);
  return { days: Math.floor(s / 86400), hours: Math.floor((s % 86400) / 3600), minutes: Math.floor((s % 3600) / 60), seconds: s % 60 };
}

export default function SiteStatusPage({ status }) {
  const { content } = useContent();
  const { company } = content;
  const mode = status.mode === "maintenance" ? "maintenance" : "coming_soon";
  const d = STATUS_DEFAULTS[mode];
  const title = status.title?.trim() || d.title;
  const message = status.message?.trim() || d.message;
  const countdown = useCountdown(mode === "coming_soon" ? status.launch_date : null);
  const Icon = mode === "maintenance" ? Wrench : Rocket;

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#0b1b2b] text-white flex flex-col">
      {/* Atmosphere: soft drifting glows + faint grid */}
      <div className="status-orb pointer-events-none absolute -top-40 -left-32 h-[520px] w-[520px] rounded-full bg-[#3E7CB1]/25 blur-3xl" />
      <div className="status-orb status-orb-b pointer-events-none absolute -bottom-48 -right-32 h-[560px] w-[560px] rounded-full bg-[#2ED9B8]/15 blur-3xl" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at center, #000 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, #000 30%, transparent 75%)",
        }}
      />

      <header className="relative z-10 flex items-center gap-3 px-6 sm:px-10 pt-8">
        <Logo size={42} />
        <div className="leading-tight">
          <p className="font-display text-xl">{company?.name?.split(" ")[0] || "Novocreste"}</p>
          <p className="text-[10px] tracking-[0.28em] text-[#2ED9B8]">INTERNATIONAL</p>
        </div>
      </header>

      <section className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 py-14">
        {/* Icon with orbiting ring */}
        <div className="relative h-24 w-24 flex items-center justify-center">
          <span className="status-ring absolute inset-0 rounded-full border border-dashed border-[#2ED9B8]/50" />
          <span className="absolute inset-3 rounded-full bg-[#132A3E] border border-white/10" />
          <Icon className="relative h-9 w-9 text-[#2ED9B8]" strokeWidth={1.6} />
        </div>

        <span className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#2ED9B8]/30 bg-[#2ED9B8]/10 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.22em] text-[#2ED9B8]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#d9a441] animate-pulse" />
          {d.pill}
        </span>

        <h1 className="mt-6 font-display text-[40px] sm:text-[56px] lg:text-[68px] leading-[1.05] tracking-tight max-w-4xl">
          {title}
        </h1>
        <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-[#9FB3C8]">{message}</p>

        {countdown && (
          <div className="mt-10 grid grid-cols-4 gap-3 sm:gap-4" role="timer" aria-label="Time until launch">
            {[["Days", countdown.days], ["Hours", countdown.hours], ["Minutes", countdown.minutes], ["Seconds", countdown.seconds]].map(([label, v]) => (
              <div key={label} className="w-[72px] sm:w-24 rounded-2xl bg-[#132A3E] border border-white/10 py-4">
                <p className="font-display text-3xl sm:text-4xl tabular-nums">{String(v).padStart(2, "0")}</p>
                <p className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[#9FB3C8]">{label}</p>
              </div>
            ))}
          </div>
        )}

        {mode === "maintenance" && (
          <div className="mt-10 h-1.5 w-56 rounded-full bg-white/10 overflow-hidden" aria-hidden="true">
            <div className="status-bar h-full w-1/3 rounded-full bg-gradient-to-r from-[#3E7CB1] to-[#2ED9B8]" />
          </div>
        )}

        <div className="mt-12 flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-sm">
          {company?.email && (
            <a href={`mailto:${company.email}`} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 px-5 h-11 transition-colors">
              <Mail className="h-4 w-4 text-[#2ED9B8]" /> {company.email}
            </a>
          )}
          {company?.phone && (
            <a href={`tel:${company.phoneHref || company.phone}`} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 px-5 h-11 transition-colors">
              <Phone className="h-4 w-4 text-[#2ED9B8]" /> {company.phone}
            </a>
          )}
        </div>
      </section>

      <footer className="relative z-10 px-6 pb-8 text-center text-xs text-[#9FB3C8]/70">
        © {new Date().getFullYear()} {company?.legalName || "Novocreste International Pvt Ltd"}
      </footer>
    </main>
  );
}
